import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Auth Pages
import Login from '@/views/pages/auth/login.vue'
import ForgotPassword from '@/views/pages/auth/forgot-password.vue'
import ResetPassword from '@/views/pages/auth/reset-password.vue'
import TwoFactorAuth from '@/views/pages/auth/two-factor-auth.vue'
import SetupTwoFactor from '@/views/pages/auth/setup-two-factor.vue'

// Error Pages
import Error404 from '@/views/pages/error/404-error.vue'
import Error500 from '@/views/pages/error/500-error.vue'

// Main Layout
import MainLayout from '@/views/layouts/main-layout.vue'

// Dashboard
import Dashboard from '@/views/pages/dashboard/dashboard.vue'

// Users
import UsersIndex from '@/views/pages/users/users-index.vue'
import UsersList from '@/views/pages/users/users-list.vue'
import AppUsersList from '@/views/pages/users/app-users-list.vue'
import AddUser from '@/views/pages/users/add-user.vue'
import EditUser from '@/views/pages/users/edit-user.vue'
import RolesPermissions from '@/views/pages/users/roles-permissions.vue'

// Notifications
import NotificationsPage from '@/views/pages/notifications/notifications.vue'

// Approvals
import ApprovalsPage from '@/views/pages/approvals/approvals.vue'

// Exams
import ExamsIndex from '@/views/pages/exams/exams-index.vue'
import ExamsList from '@/views/pages/exams/exams-list.vue'
import AddExam from '@/views/pages/exams/add-exam.vue'
import EditExam from '@/views/pages/exams/edit-exam.vue'
import ExamQuestions from '@/views/pages/exams/exam-questions.vue'

// Courses
import CoursesIndex from '@/views/pages/courses/courses-index.vue'
import CoursesList from '@/views/pages/courses/courses-list.vue'
import AddCourse from '@/views/pages/courses/add-course.vue'
import EditCourse from '@/views/pages/courses/edit-course.vue'

// Syllabus
import SyllabusIndex from '@/views/pages/syllabus/syllabus-index.vue'
import SyllabusList from '@/views/pages/syllabus/syllabus-list.vue'
import AddSyllabus from '@/views/pages/syllabus/add-syllabus.vue'
import EditSyllabus from '@/views/pages/syllabus/edit-syllabus.vue'

// Notes
import NotesIndex from '@/views/pages/notes/notes-index.vue'
import NotesList from '@/views/pages/notes/notes-list.vue'
import AddNote from '@/views/pages/notes/add-note.vue'
import EditNote from '@/views/pages/notes/edit-note.vue'

// Questions
import QuestionsIndex from '@/views/pages/questions/questions-index.vue'
import QuestionsList from '@/views/pages/questions/questions-list.vue'
import AddQuestion from '@/views/pages/questions/add-question.vue'
import EditQuestion from '@/views/pages/questions/edit-question.vue'

// Subjects
import SubjectsIndex from '@/views/pages/subjects/subjects-index.vue'
import SubjectsList from '@/views/pages/subjects/subjects-list.vue'
import AddSubject from '@/views/pages/subjects/add-subject.vue'
import EditSubject from '@/views/pages/subjects/edit-subject.vue'

// Topics
import TopicsIndex from '@/views/pages/topics/topics-index.vue'
import TopicsList from '@/views/pages/topics/topics-list.vue'
import AddTopic from '@/views/pages/topics/add-topic.vue'
import EditTopic from '@/views/pages/topics/edit-topic.vue'

// Lessons
import LessonsIndex from '@/views/pages/lessons/lessons-index.vue'
import LessonsList from '@/views/pages/lessons/lessons-list.vue'
import AddLesson from '@/views/pages/lessons/add-lesson.vue'
import EditLesson from '@/views/pages/lessons/edit-lesson.vue'

// Test Series
import TestSeriesIndex from '@/views/pages/test-series/test-series-index.vue'
import TestSeriesList from '@/views/pages/test-series/test-series-list.vue'
import AddTestSeries from '@/views/pages/test-series/add-test-series.vue'
import EditTestSeries from '@/views/pages/test-series/edit-test-series.vue'

// Tests
import TestsIndex from '@/views/pages/tests/tests-index.vue'
import TestsList from '@/views/pages/tests/tests-list.vue'
import AddTest from '@/views/pages/tests/add-test.vue'
import EditTest from '@/views/pages/tests/edit-test.vue'
import TestQuestions from '@/views/pages/tests/test-questions.vue'
import TestResults from '@/views/pages/tests/test-results.vue'

// Flashcards
import FlashcardsIndex from '@/views/pages/flashcards/flashcards-index.vue'
import FlashcardsList from '@/views/pages/flashcards/flashcards-list.vue'

// Bulletins
import BulletinsIndex from '@/views/pages/bulletins/bulletins-index.vue'
import BulletinsList from '@/views/pages/bulletins/bulletins-list.vue'

// Pattern Insights
import PatternInsightsIndex from '@/views/pages/pattern-insights/pattern-insights-index.vue'
import PatternInsightsList from '@/views/pages/pattern-insights/pattern-insights-list.vue'

// Reports
import ReportsPage from '@/views/pages/reports/reports.vue'

// TestPal AI
import TestPalAIPage from '@/views/pages/testpal-ai/testpal-ai.vue'

// Settings
import SettingsIndex from '@/views/pages/settings/settings-index.vue'
import ProfileSettings from '@/views/pages/settings/profile-settings.vue'
import SecuritySettings from '@/views/pages/settings/security-settings.vue'
import AppSettings from '@/views/pages/settings/app-settings.vue'

const routes = [
  // Auth Routes (no layout)
  {
    path: '/',
    name: 'login',
    component: Login,
    meta: { requiresGuest: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
    meta: { requiresGuest: true }
  },
  {
    path: '/two-factor-auth',
    name: 'two-factor-auth',
    component: TwoFactorAuth,
    meta: { requires2FA: true }
  },
  {
    path: '/setup-two-factor',
    name: 'setup-two-factor',
    component: SetupTwoFactor,
    meta: { requiresSetup2FA: true }
  },
  
  // Error Pages
  {
    path: '/404',
    name: '404-error',
    component: Error404
  },
  {
    path: '/500',
    name: '500-error',
    component: Error500
  },

  // Protected Routes (with main layout)
  {
    path: '/app',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/app/dashboard'
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: Dashboard
      },
      
      // Users - restricted to admin and super_admin
      {
        path: 'users',
        component: UsersIndex,
        meta: { allowedRoles: ['super_admin', 'admin'] },
        children: [
          { path: '', redirect: '/app/users/list' },
          { path: 'list', name: 'users-list', component: UsersList },
          { path: 'app-users', name: 'app-users-list', component: AppUsersList },
          { path: 'add', name: 'add-user', component: AddUser, meta: { allowedRoles: ['super_admin'] } },
          { path: 'edit/:id', name: 'edit-user', component: EditUser, meta: { allowedRoles: ['super_admin'] } },
          { path: 'roles', name: 'roles-permissions', component: RolesPermissions }
        ]
      },

      // Notifications
      {
        path: 'notifications',
        name: 'notifications',
        component: NotificationsPage
      },

      // Approvals
      {
        path: 'approvals',
        name: 'approvals',
        component: ApprovalsPage
      },

      // Exams
      {
        path: 'exams',
        component: ExamsIndex,
        children: [
          { path: '', redirect: '/app/exams/list' },
          { path: 'list', name: 'exams-list', component: ExamsList },
          { path: 'add', name: 'add-exam', component: AddExam },
          { path: 'edit/:id', name: 'edit-exam', component: EditExam },
          { path: ':id/questions', name: 'exam-questions', component: ExamQuestions },
          { path: ':examId/subjects', name: 'exam-subjects', component: SubjectsList },
          { path: ':examId/subjects/add', name: 'add-subject', component: AddSubject },
          { path: ':examId/test-series', name: 'exam-test-series', component: TestSeriesList },
          { path: ':examId/test-series/add', name: 'add-test-series', component: AddTestSeries }
        ]
      },

      // Subjects (standalone routes)
      {
        path: 'subjects',
        component: SubjectsIndex,
        children: [
          { path: 'edit/:id', name: 'edit-subject', component: EditSubject },
          { path: ':subjectId/topics', name: 'subject-topics', component: TopicsList },
          { path: ':subjectId/topics/add', name: 'add-topic', component: AddTopic }
        ]
      },

      // Topics (standalone routes)
      {
        path: 'topics',
        component: TopicsIndex,
        children: [
          { path: 'edit/:id', name: 'edit-topic', component: EditTopic },
          { path: ':topicId/lessons', name: 'topic-lessons', component: LessonsList },
          { path: ':topicId/lessons/add', name: 'add-lesson', component: AddLesson },
          { path: ':topicId/notes', name: 'topic-notes', component: NotesList },
          { path: ':topicId/tests', name: 'topic-tests', component: TestsList },
          { path: ':topicId/tests/add', name: 'add-topic-test', component: AddTest }
        ]
      },

      // Lessons (standalone routes)
      {
        path: 'lessons',
        component: LessonsIndex,
        children: [
          { path: 'edit/:id', name: 'edit-lesson', component: EditLesson },
          { path: ':lessonId/tests', name: 'lesson-tests', component: TestsList },
          { path: ':lessonId/tests/add', name: 'add-lesson-test', component: AddTest },
          { path: ':lessonId/notes', name: 'lesson-notes', component: NotesList }
        ]
      },

      // Test Series (standalone routes)
      {
        path: 'test-series',
        component: TestSeriesIndex,
        children: [
          { path: 'edit/:id', name: 'edit-test-series', component: EditTestSeries },
          { path: ':seriesId/tests', name: 'series-tests', component: TestsList },
          { path: ':seriesId/tests/add', name: 'add-series-test', component: AddTest }
        ]
      },

      // Tests (standalone routes)
      {
        path: 'tests',
        component: TestsIndex,
        children: [
          { path: 'edit/:id', name: 'edit-test', component: EditTest },
          { path: ':testId/questions', name: 'test-questions', component: TestQuestions },
          { path: ':testId/results', name: 'test-results', component: TestResults }
        ]
      },

      // Flashcards
      {
        path: 'flashcards',
        component: FlashcardsIndex,
        children: [
          { path: '', redirect: '/app/flashcards/list' },
          { path: 'list', name: 'flashcards-list', component: FlashcardsList }
        ]
      },

      // Bulletins
      {
        path: 'bulletins',
        component: BulletinsIndex,
        children: [
          { path: '', redirect: '/app/bulletins/list' },
          { path: 'list', name: 'bulletins-list', component: BulletinsList }
        ]
      },

      // Pattern Insights
      {
        path: 'pattern-insights',
        component: PatternInsightsIndex,
        children: [
          { path: '', redirect: '/app/pattern-insights/list' },
          { path: 'list', name: 'pattern-insights-list', component: PatternInsightsList }
        ]
      },

      // Courses (legacy - can be removed later)
      {
        path: 'courses',
        component: CoursesIndex,
        children: [
          { path: '', redirect: '/app/courses/list' },
          { path: 'list', name: 'courses-list', component: CoursesList },
          { path: 'add', name: 'add-course', component: AddCourse },
          { path: 'edit/:id', name: 'edit-course', component: EditCourse }
        ]
      },

      // Syllabus (legacy - can be removed later)
      {
        path: 'syllabus',
        component: SyllabusIndex,
        children: [
          { path: '', redirect: '/app/syllabus/list' },
          { path: 'list', name: 'syllabus-list', component: SyllabusList },
          { path: 'add', name: 'add-syllabus', component: AddSyllabus },
          { path: 'edit/:id', name: 'edit-syllabus', component: EditSyllabus }
        ]
      },

      // Notes (standalone - for general notes management)
      {
        path: 'notes',
        component: NotesIndex,
        children: [
          { path: '', redirect: '/app/notes/list' },
          { path: 'list', name: 'notes-list', component: NotesList },
          { path: 'add', name: 'add-note', component: AddNote },
          { path: 'edit/:id', name: 'edit-note', component: EditNote }
        ]
      },

      // Questions
      {
        path: 'questions',
        component: QuestionsIndex,
        children: [
          { path: '', redirect: '/app/questions/list' },
          { path: 'list', name: 'questions-list', component: QuestionsList },
          { path: 'add', name: 'add-question', component: AddQuestion },
          { path: 'edit/:id', name: 'edit-question', component: EditQuestion }
        ]
      },

      // Reports
      {
        path: 'reports',
        name: 'reports',
        component: ReportsPage
      },

      // TestPal AI
      {
        path: 'testpal-ai',
        name: 'testpal-ai',
        component: TestPalAIPage
      },

      // Settings
      {
        path: 'settings',
        component: SettingsIndex,
        children: [
          { path: '', redirect: '/app/settings/profile' },
          { path: 'profile', name: 'profile-settings', component: ProfileSettings },
          { path: 'security', name: 'security-settings', component: SecuritySettings },
          { path: 'app', name: 'app-settings', component: AppSettings }
        ]
      }
    ]
  },

  // Catch all - redirect to 404
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

// Track if initial auth check is done
let isAuthInitialized = false
let authInitPromise = null

/**
 * Initialize authentication state
 * Returns a promise that resolves when auth is ready
 */
async function initializeAuth() {
  if (isAuthInitialized) {
    return
  }

  if (authInitPromise) {
    return authInitPromise
  }

  authInitPromise = (async () => {
    try {
      const authStore = useAuthStore()
      await authStore.initialize()
    } catch (error) {
      console.error('Auth initialization failed:', error)
    } finally {
      isAuthInitialized = true
    }
  })()

  return authInitPromise
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  // Initialize auth on first navigation
  await initializeAuth()

  const authStore = useAuthStore()
  
  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)
  const requires2FA = to.matched.some(record => record.meta.requires2FA)
  const requiresSetup2FA = to.matched.some(record => record.meta.requiresSetup2FA)

  // Handle protected routes
  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Not authenticated, redirect to login
      return next({ 
        name: 'login', 
        query: { redirect: to.fullPath } 
      })
    }

    // If user still needs to set up 2FA, redirect them back to setup
    if (authStore.requiresSetup2FA) {
      return next({ name: 'setup-two-factor' })
    }

    // Check if token needs refresh
    if (authStore.shouldRefreshToken()) {
      try {
        await authStore.refreshAccessToken()
      } catch (error) {
        console.warn('Token refresh failed in navigation guard:', error)
        return next({ name: 'login' })
      }
    }

    // Check role-based access
    const allowedRoles = to.matched
      .filter(record => record.meta.allowedRoles)
      .map(record => record.meta.allowedRoles)
      .pop() // Use the most specific (deepest) route's allowedRoles

    if (allowedRoles && allowedRoles.length > 0) {
      const userRole = authStore.userRole
      if (!allowedRoles.includes(userRole)) {
        return next({ name: 'dashboard' })
      }
    }
  }

  // Handle guest-only routes (login, forgot password, etc.)
  if (requiresGuest && authStore.isAuthenticated) {
    return next({ name: 'dashboard' })
  }

  // Handle 2FA verification route
  if (requires2FA && !authStore.pending2FA) {
    return next({ name: 'login' })
  }

  // Handle 2FA setup route
  if (requiresSetup2FA && !authStore.requiresSetup2FA) {
    // If authenticated but doesn't need setup, go to dashboard
    if (authStore.isAuthenticated) {
      return next({ name: 'dashboard' })
    }
    return next({ name: 'login' })
  }

  next()
})

// Handle navigation errors
router.onError((error) => {
  console.error('Router error:', error)
  
  // Handle chunk loading errors (common in production with code splitting)
  if (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk')) {
    window.location.reload()
  }
})

export default router
